import {FC, useEffect, useState} from 'react';

import {
    Box, Card,
    Container,
    Grid,
    InputAdornment,
    Table, TableBody, TableCell,
    TableContainer,
    TableHead, TablePagination, TableRow,
    TextField,
    Typography, Button, Popover, MenuItem
} from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import {Alert} from '@deere/fuel-react';
import SearchIcon from '@mui/icons-material/Search';
import TableSortLabel from '@mui/material/TableSortLabel';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {getFilteredScheduledClasses} from '../components/SearchBar';
import {useOktaAuth} from '@okta/okta-react';
import './ScheduledClassHistory.css';
import {ClassAssessment} from './ClassAssessment';
import {ClassAssessmentHistory} from './ClassAssessmentHistory';

interface ClassItem {
    courseTitle: string;
    classId: string;
    classStartDate: string;
    classEndDate: string;
    hasClassStarted: string;
}

const LOADER_ROW_COUNT = 3;
const DEFAULT_ROWS_PER_PAGE = 25;
const ROWS_PER_PAGE_25 = 25;
const ROWS_PER_PAGE_50 = 50;
const ROWS_PER_PAGE_100 = 100;
const ROWS_PER_PAGE_OPTIONS = [ROWS_PER_PAGE_25, ROWS_PER_PAGE_50, ROWS_PER_PAGE_100];

const handleChangeRowsPerPage = (value: any, setState: any): void => {
    setState((prev: any) => ({
        ...prev,
        table: {
            ...prev.table,
            rowsPerPage: parseInt(value, 10),
            page: 0
        }
    }));
};

const handleLoader = (isLoading: boolean): JSX.Element => {
    if (!isLoading) {
        return <Typography variant='h6'>{'No Scheduled Classes Found'}</Typography>;
    }
    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        {['Class Id', 'Course Title', 'Class Start Date', 'Class End Date', 'Action'].map((header) => (
                            <TableCell key={header}>
                                <Skeleton
                                    height={60}
                                    variant='text'
                                />
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {[...Array(LOADER_ROW_COUNT)].map((_, index) => (
                        <TableRow key={index}>
                            <TableCell>
                                <Skeleton
                                    height={60}
                                    variant='text'
                                />
                            </TableCell>
                            <TableCell>
                                <Skeleton
                                    height={60}
                                    variant='text'
                                />
                            </TableCell>
                            <TableCell>
                                <Skeleton
                                    height={60}
                                    variant='text'
                                />
                            </TableCell>
                            <TableCell>
                                <Skeleton
                                    height={60}
                                    variant='text'
                                />
                            </TableCell>
                            <TableCell>
                                <Skeleton
                                    height={60}
                                    variant='text'
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const handlePopoverOpen = (event: React.MouseEvent<HTMLButtonElement>, classId: string, courseTitle: string, setState: any): void => {
    setState((prev: any) => ({
        ...prev,
        anchorEl: event.target,
        popoverClassId: classId,
        popoverCourseId: courseTitle ? courseTitle.split(':')[0] : ''
    }));
};

const handleDialogSubmitAssessment = (alertMessage: string, alertType: string, setState: any): void => {
    const MAX_ALERT_DISPLAY_TIME = 5000;

    setState((prev: any) => ({
        ...prev,
        isDialogOpen: false,
        activeAlert: true,
        alertMessage,
        alertType
    }));
    setTimeout(() => {
        setState((prevState: any) => ({
            ...prevState,
            activeAlert: false,
            alertMessage: ''
        }));
    },
    MAX_ALERT_DISPLAY_TIME);
};

const renderAlert = (activeAlert: boolean, alertType: 'success' | 'error', alertMessage: string): JSX.Element | null => {
    if (!activeAlert) {
        return null;
    }

    return (
        <Alert
            title=''
            variant={alertType}
        >
            {alertMessage}
        </Alert>
    );
};

const handleDialogClose = (state: any, setState: any): void => {
    setState((prev: any) => ({
        ...prev,
        isDialogOpen: false
    }));
};

const handlePopoverClose = (setState: any): void => {
    setState((prev: any) => ({
        ...prev,
        anchorEl: null
    }));
};

export const ScheduledClassHistory: FC<unknown> = () => {
    const {authState} = useOktaAuth();
    const [state, setState] = useState({
        classes: [] as ClassItem[],
        table: {
            page: 0,
            rowsPerPage: DEFAULT_ROWS_PER_PAGE,
            order: 'desc' as 'asc' | 'desc',
            orderBy: 'classStartDate' as keyof ClassItem,
            searchTerm: ''
        },
        anchorEl: null as HTMLElement | null,
        popoverClassId: '',
        popoverCourseId: '',
        isDialogOpen: false,
        isHistoryDialogOpen: false,
        activeAlert: false,
        alertMessage: '',
        alertType: 'success' as 'success' | 'error',
        isLoading: true
    });

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/scheduledClasses`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authState?.accessToken?.accessToken}`,
                'Content-Type': 'application/json; charset=UTF-8',
                'Accept': 'application/json'
            }
        })
            .then((res) => res.json())
            .then((data) => {
                if (Array.isArray(data.message)) {
                    const formattedData = data.message.map((item: any) => ({
                        courseTitle: item.course_title,
                        classId: item.class_id,
                        classStartDate: item.class_start_date,
                        classEndDate: item.class_end_date,
                        hasClassStarted: item.has_class_started
                    }));

                    setState((prev) => ({
                        ...prev,
                        classes: formattedData,
                        isLoading: false
                    }));
                }
                return data;
            })
            .catch((error) => {
                console.error('Error fetching classes:', error);
            });
    }, [authState]);

    const handleChangePage = (event: unknown, newPage: number): void => {
        setState((prev) => ({
            ...prev,
            table: {
                ...prev.table,
                page: newPage
            }
        }));
    };

    const handleRequestSort = (property: keyof ClassItem): void => {
        const isAsc = state.table.orderBy === property && state.table.order === 'asc';

        setState((prev) => ({
            ...prev,
            table: {
                ...prev.table,
                order: isAsc ? 'desc' : 'asc',
                orderBy: property
            }
        }));
    };

    const handleDialogOpen = (): void => {
        setState((prev) => ({
            ...prev,
            isDialogOpen: true
        }));
    };

    const handleHistoryDialogOpen = (): void => {
        setState((prev) => ({
            ...prev,
            isHistoryDialogOpen: true
        }));
    };

    const handleHistoryDialogClose = (): void => {
        setState((prev) => ({
            ...prev,
            isHistoryDialogOpen: false
        }));
    };

    const sortedClasses = getFilteredScheduledClasses(state.classes, state.table.searchTerm).sort((a, b) => {
        const isDate = ['classStartDate', 'classEndDate'].includes(state.table.orderBy);

        return isDate ? new Date(a[state.table.orderBy]).getTime() - new Date(b[state.table.orderBy]).getTime() : a[state.table.orderBy].localeCompare(b[state.table.orderBy]);
    });

    return (
        <Container
            sx={
                {
                    paddingTop: '1rem',
                    textAlign: 'center'
                }
            }
        >
            {renderAlert(state.activeAlert, state.alertType, state.alertMessage)}
            <Grid
                alignItems='center'
                container
                justifyContent='space-between'
            >
                <Grid item>
                    <Typography variant='h4'>{'Scheduled Class List'}</Typography>
                </Grid>
            </Grid>
            <Grid
                container
                justifyContent='space-between'
                sx={{
                    paddingTop: '1rem'
                }}
            >
                <Grid item>
                    <TextField
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <SearchIcon/>
                                </InputAdornment>
                            )
                        }}
                        onChange={(e): any => setState((prev) => ({
                            ...prev,
                            table: {
                                ...prev.table,
                                searchTerm: e.target.value,
                                page: 0
                            }
                        }))}
                        placeholder='Search'
                        size='small'
                        sx={
                            {
                                width: '100%',
                                maxWidth: '300px',
                                textAlign: 'left'
                            }
                        }
                        value={state.table.searchTerm}
                        variant='outlined'
                    />
                </Grid>
            </Grid>
            <Card
                sx={
                    {
                        marginTop: '1rem',
                        overflow: 'hidden'
                    }
                }
            >
                {state.classes.length > 0 ?
                    <>
                        <TableContainer
                            sx={
                                {
                                    maxWidth: '100%',
                                    overflowX: 'auto'
                                }
                            }
                        >
                            <Table
                                sx={
                                    {
                                        minWidth: '650px'
                                    }
                                }
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <TableSortLabel
                                                active={state.table.orderBy === 'classId'}
                                                direction={state.table.order}
                                                onClick={(): void => handleRequestSort('classId')}
                                            >
                                                {'Class Id'}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={state.table.orderBy === 'courseTitle'}
                                                direction={state.table.order}
                                                onClick={(): void => handleRequestSort('courseTitle')}
                                            >
                                                {'Course Title'}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={state.table.orderBy === 'classStartDate'}
                                                direction={state.table.order}
                                                onClick={(): void => handleRequestSort('classStartDate')}
                                            >
                                                {'Class Start Date'}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={state.table.orderBy === 'classEndDate'}
                                                direction={state.table.order}
                                                onClick={(): void => handleRequestSort('classEndDate')}
                                            >
                                                {'Class End Date'}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>{'Action'}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedClasses.slice(state.table.page * state.table.rowsPerPage, state.table.page * state.table.rowsPerPage + state.table.rowsPerPage).map((classItem) => (
                                        <TableRow key={classItem.classId}>
                                            <TableCell>{classItem.classId}</TableCell>
                                            <TableCell>{classItem.courseTitle}</TableCell>
                                            <TableCell>{classItem.classStartDate}</TableCell>
                                            <TableCell>{classItem.classEndDate}</TableCell>
                                            <TableCell>
                                                <Button
                                                    aria-describedby={`popover-${classItem.classId}`}
                                                    disabled={classItem.hasClassStarted !== 'Yes'}
                                                    endIcon={<ArrowDropDownIcon/>}
                                                    id={`popover-button-id-${classItem.classId}`}
                                                    onClick={(e): void => handlePopoverOpen(e, classItem.classId, classItem.courseTitle, setState)}
                                                >
                                                    {'Assign Assessment'}
                                                </Button>
                                                <Popover
                                                    anchorEl={state.anchorEl}
                                                    anchorOrigin={{
                                                        vertical: 'bottom',
                                                        horizontal: 'left'
                                                    }}
                                                    id={`popover-${classItem.classId}`}
                                                    key={state.popoverClassId} // Forces remount when class changes
                                                    onClose={(): void => handlePopoverClose(setState)}
                                                    open={Boolean(state.anchorEl) && state.popoverClassId === classItem.classId}
                                                    transformOrigin={{
                                                        vertical: 'top',
                                                        horizontal: 'left'
                                                    }}
                                                >
                                                    <MenuItem
                                                        onClick={(): void => {
                                                            handlePopoverClose(setState);
                                                            handleDialogOpen();
                                                        }}
                                                    >
                                                        {'Assign Assessment'}
                                                    </MenuItem>
                                                    <MenuItem
                                                        onClick={(): void => {
                                                            handlePopoverClose(setState);
                                                            handleHistoryDialogOpen();
                                                        }}
                                                    >
                                                        {'History'}
                                                    </MenuItem>
                                                </Popover>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component='div'
                            count={sortedClasses.length}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={(e): void => handleChangeRowsPerPage(e.target.value, setState)}
                            page={state.table.page}
                            rowsPerPage={state.table.rowsPerPage}
                            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                        />
                    </> :
                    <Card>
                        <Box
                            sx={{
                                padding: '2rem',
                                textAlign: 'center'
                            }}
                        >
                            {handleLoader(state.isLoading)}
                        </Box>
                    </Card>
                }
            </Card>
            <ClassAssessmentHistory
                DialogOpen={state.isHistoryDialogOpen}
                classId={state.popoverClassId}
                courseId={state.popoverCourseId}
                handleDialogClose={handleHistoryDialogClose}
            />
            <ClassAssessment
                DialogOpen={state.isDialogOpen}
                classId={state.popoverClassId}
                courseId={state.popoverCourseId}
                handleDialogClose={(): any => handleDialogClose(state, setState)}
                handleDialogSubmitAssessment={(alertMessage: string, alertType: string): void => handleDialogSubmitAssessment(alertMessage, alertType, setState)}
            />
        </Container>
    );
};
